/**
 * src/lib/spotlightModel.js: hồ sơ một trang Startup Spotlight gửi riêng cho quỹ.
 *
 * Hai lớp nội dung, tách bạch:
 *  - Bài Spotlight công khai (Playbook): kể chuyện sản phẩm và founder, KHÔNG có
 *    điều khoản gọi vốn.
 *  - Hồ sơ một trang (file này): thêm "Vòng đang gọi" và "Góc nhìn fundraising.vn".
 *    Chỉ lưu ở collection spotlight_ho_so (chỉ quản trị đọc được) và chỉ đi ra
 *    ngoài dưới dạng PDF do quản trị tự gửi cho từng quỹ.
 *
 * Trang quản trị nhập liệu bằng ô chữ (mỗi dòng một mục, các ô cách nhau bằng |)
 * giống quy ước bảng của Playbook. formSangHoSo và hoSoSangForm đổi qua lại.
 *
 * File thuần JS, không import gì, để chạy được bằng node --test.
 */

export const LIEN_HE_MAC_DINH = 'xin-chao@fundraising.vn';

export const NGUON = {
  'tu-khai': 'Founder tự khai',
  'doi-chieu': 'Đã đối chiếu',
};

const cat = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
const catDoan = (v, max) => String(v ?? '').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim().slice(0, max);
const dong = (chu) => String(chu ?? '').split('\n').map((x) => x.trim()).filter(Boolean);
const o = (d) => d.split('|').map((x) => x.trim());

/** Đọc nguồn số liệu từ chữ tự do: "đã đối chiếu", "doi-chieu", "đối chiếu" đều nhận. */
export function docNguon(chu) {
  const t = String(chu ?? '').toLowerCase();
  return /đối chiếu|doi[- ]chieu|xác minh|kiểm chứng/.test(t) ? 'doi-chieu' : 'tu-khai';
}

export const HO_SO_TRONG = {
  ten: '', dinhVi: '', nganh: '', giaiDoan: '', truSo: '', thanhLap: '', website: '',
  dot: '', vanDe: '', giaiPhap: '', linkDemo: '',
  chiSo: [], doiNgu: [], soNhanSu: '',
  vong: { quyMo: '', hinhThuc: '', dinhGia: '', daCamKet: '', runway: '', dungVon: [] },
  gocNhin: { diem: null, ngayCham: '', diemManh: [], cauHoi: [] },
  lienHe: LIEN_HE_MAC_DINH,
  baiSlug: '', deckReviewId: '', viDu: false,
};

/** Chuẩn hóa mọi dữ liệu vào (Firestore, form, ví dụ). Giới hạn độ dài để vừa một trang A4. */
export function chuanHoaHoSo(raw = {}) {
  const v = raw.vong || {};
  const g = raw.gocNhin || {};
  const diem = Number(g.diem);
  return {
    ten: cat(raw.ten, 60),
    dinhVi: cat(raw.dinhVi, 160),
    nganh: cat(raw.nganh, 40),
    giaiDoan: cat(raw.giaiDoan, 30),
    truSo: cat(raw.truSo, 40),
    thanhLap: cat(raw.thanhLap, 10),
    website: cat(raw.website, 60),
    dot: cat(raw.dot, 60),
    vanDe: catDoan(raw.vanDe, 360),
    giaiPhap: catDoan(raw.giaiPhap, 360),
    linkDemo: /^https:\/\//.test(String(raw.linkDemo || '').trim()) ? cat(raw.linkDemo, 300) : '',
    chiSo: (Array.isArray(raw.chiSo) ? raw.chiSo : []).slice(0, 4)
      .map((x) => ({ giaTri: cat(x?.giaTri, 18), nhan: cat(x?.nhan, 60), thoiDiem: cat(x?.thoiDiem, 20), nguon: x?.nguon in NGUON ? x.nguon : 'tu-khai' }))
      .filter((x) => x.giaTri || x.nhan),
    doiNgu: (Array.isArray(raw.doiNgu) ? raw.doiNgu : []).slice(0, 3)
      .map((x) => ({ ten: cat(x?.ten, 50), vaiTro: cat(x?.vaiTro, 30), gioiThieu: cat(x?.gioiThieu, 160) }))
      .filter((x) => x.ten),
    soNhanSu: cat(raw.soNhanSu, 10),
    vong: {
      quyMo: cat(v.quyMo, 40), hinhThuc: cat(v.hinhThuc, 40), dinhGia: cat(v.dinhGia, 40),
      daCamKet: cat(v.daCamKet, 60), runway: cat(v.runway, 30),
      dungVon: (Array.isArray(v.dungVon) ? v.dungVon : []).slice(0, 4)
        .map((x) => ({ muc: cat(x?.muc, 30), pt: Math.max(0, Math.min(100, Math.round(Number(x?.pt) || 0))) }))
        .filter((x) => x.muc && x.pt > 0),
    },
    gocNhin: {
      diem: Number.isFinite(diem) && g.diem !== '' && g.diem !== null ? Math.max(0, Math.min(100, Math.round(diem))) : null,
      ngayCham: cat(g.ngayCham, 20),
      diemManh: dong((Array.isArray(g.diemManh) ? g.diemManh : []).join('\n')).slice(0, 3).map((x) => cat(x, 130)),
      cauHoi: dong((Array.isArray(g.cauHoi) ? g.cauHoi : []).join('\n')).slice(0, 3).map((x) => cat(x, 130)),
    },
    lienHe: /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(String(raw.lienHe || '').trim()) ? cat(raw.lienHe, 80) : LIEN_HE_MAC_DINH,
    baiSlug: cat(raw.baiSlug, 120),
    deckReviewId: cat(raw.deckReviewId, 60),
    viDu: raw.viDu === true,
  };
}

/** Những điểm nên sửa trước khi gửi quỹ. Không chặn xuất PDF, chỉ nhắc. */
export function kiemTraHoSo(h) {
  const loi = [];
  if (!h.ten) loi.push('Chưa có tên startup.');
  const soChu = h.dinhVi.split(/\s+/).filter(Boolean).length;
  if (!h.dinhVi) loi.push('Chưa có câu định vị.');
  else if (soChu > 20) loi.push(`Câu định vị đang ${soChu} chữ, nên rút còn 20 chữ trở xuống.`);
  if (!h.vanDe || !h.giaiPhap) loi.push('Thiếu phần Vấn đề hoặc Giải pháp.');
  if (h.chiSo.length < 2) loi.push('Nên có ít nhất 2 chỉ số tiến triển.');
  if (h.chiSo.length && !h.chiSo.some((x) => x.nguon === 'doi-chieu')) loi.push('Chưa có chỉ số nào được đối chiếu. Quỹ sẽ hỏi ngay điểm này.');
  if (!h.doiNgu.length) loi.push('Chưa có đội ngũ sáng lập.');
  if (!h.vong.quyMo) loi.push('Chưa ghi quy mô vòng đang gọi.');
  const tong = h.vong.dungVon.reduce((a, b) => a + b.pt, 0);
  if (h.vong.dungVon.length && tong !== 100) loi.push(`Tỷ lệ dùng vốn cộng lại ${tong}%, chưa đủ 100%.`);
  if (h.gocNhin.diem === null) loi.push('Chưa có điểm rubric từ nhận xét deck.');
  if (h.viDu) loi.push('Đây là hồ sơ ví dụ: PDF có dải "VÍ DỤ MINH HỌA", không dùng để gửi quỹ.');
  return loi;
}

/* ── Đổi qua lại giữa hồ sơ và ô nhập trên trang quản trị ───────────── */

export function hoSoSangForm(raw) {
  const h = chuanHoaHoSo(raw);
  return {
    ...h,
    chiSoText: h.chiSo.map((x) => [x.giaTri, x.nhan, x.thoiDiem, NGUON[x.nguon]].join(' | ')).join('\n'),
    doiNguText: h.doiNgu.map((x) => [x.ten, x.vaiTro, x.gioiThieu].join(' | ')).join('\n'),
    dungVonText: h.vong.dungVon.map((x) => `${x.muc} | ${x.pt}`).join('\n'),
    diemText: h.gocNhin.diem === null ? '' : String(h.gocNhin.diem),
    diemManhText: h.gocNhin.diemManh.join('\n'),
    cauHoiText: h.gocNhin.cauHoi.join('\n'),
  };
}

export function formSangHoSo(f) {
  return chuanHoaHoSo({
    ...f,
    chiSo: dong(f.chiSoText).map((d) => { const [giaTri, nhan, thoiDiem, nguon] = o(d); return { giaTri, nhan, thoiDiem, nguon: docNguon(nguon) }; }),
    doiNgu: dong(f.doiNguText).map((d) => { const [ten, vaiTro, ...con] = o(d); return { ten, vaiTro, gioiThieu: con.join(', ') }; }),
    vong: { ...f.vong, dungVon: dong(f.dungVonText).map((d) => { const [muc, pt] = o(d); return { muc, pt: parseFloat(String(pt).replace(',', '.')) }; }) },
    gocNhin: { ...f.gocNhin, diem: f.diemText === '' ? null : f.diemText, diemManh: dong(f.diemManhText), cauHoi: dong(f.cauHoiText) },
  });
}

/* ── Nạp từ dữ liệu sẵn có, để khỏi gõ lại ───────────────────────────── */

const vanBanPhan = (s) => [...(s?.paragraphs || []), ...(s?.bullets || [])].map((x) => String(x).trim()).filter(Boolean).join('\n\n');

/**
 * Lấy phần công khai từ bài Spotlight trong Playbook. Chỉ ghi vào ô đang trống
 * để không đè phần đã biên tập riêng cho quỹ.
 */
export function tuBaiSpotlight(post, hienTai = HO_SO_TRONG) {
  const theoTieuDe = new Map((post?.sections || []).map((s) => [String(s.heading || '').trim().toLowerCase(), s]));
  const phan = (ten) => theoTieuDe.get(ten.toLowerCase());
  const [nganh = '', giaiDoan = ''] = String(post?.authorRole || '').split(/[,·]/).map((x) => x.trim());
  const bang = phan('Bằng chứng tiến triển')?.table;
  const chiSo = (bang?.rows || []).map((r) => ({ nhan: r[0], giaTri: r[1], thoiDiem: r[2], nguon: docNguon(r[3]) }))
    .filter((x) => x.giaTri && x.nhan);
  const doiNgu = (phan('Đội ngũ sáng lập')?.bullets || []).map((b) => {
    const [ten, vaiTro, ...con] = String(b).split(',').map((x) => x.trim());
    return { ten, vaiTro, gioiThieu: con.join(', ') };
  }).filter((x) => x.ten);
  const h = chuanHoaHoSo(hienTai);
  const neuTrong = (cu, moi) => (cu && (!Array.isArray(cu) || cu.length) ? cu : moi);
  return chuanHoaHoSo({
    ...h,
    ten: neuTrong(h.ten, post?.author),
    dinhVi: neuTrong(h.dinhVi, post?.excerpt),
    nganh: neuTrong(h.nganh, nganh),
    giaiDoan: neuTrong(h.giaiDoan, giaiDoan),
    vanDe: neuTrong(h.vanDe, vanBanPhan(phan('Vấn đề'))),
    giaiPhap: neuTrong(h.giaiPhap, vanBanPhan(phan('Sản phẩm giải quyết thế nào'))),
    chiSo: neuTrong(h.chiSo, chiSo),
    doiNgu: neuTrong(h.doiNgu, doiNgu),
    baiSlug: post?.slug || h.baiSlug,
  });
}

/** Lấy điểm rubric, điểm mạnh và câu hỏi mở từ nhận xét pitch deck đã biên tập. */
export function tuNhanXet(nx, yc = {}, hienTai = HO_SO_TRONG, homNay = new Date()) {
  const h = chuanHoaHoSo(hienTai);
  const cauHoi = [
    ...(nx?.diemDung || []).map((x) => x.chuDe),
    ...(nx?.cauHoi || []),
  ].filter(Boolean);
  const ngay = `${String(homNay.getDate()).padStart(2, '0')}/${String(homNay.getMonth() + 1).padStart(2, '0')}/${homNay.getFullYear()}`;
  return chuanHoaHoSo({
    ...h,
    ten: h.ten || yc.congTy,
    nganh: h.nganh || yc.nganh,
    giaiDoan: h.giaiDoan || (yc.vong && yc.vong !== 'Chưa chắc' ? yc.vong : ''),
    gocNhin: {
      diem: nx?.diemTong ?? null,
      ngayCham: ngay,
      diemManh: (nx?.diemManh || []).slice(0, 2),
      cauHoi: cauHoi.slice(0, 2),
    },
    deckReviewId: yc.id || h.deckReviewId,
  });
}

/** Tên file PDF không dấu. */
export function tenTepPdf(h) {
  const slug = String(h.ten || 'startup').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'startup';
  return `spotlight-${slug}${h.viDu ? '-vi-du' : ''}.pdf`;
}

/**
 * Hồ sơ ví dụ để thử bố cục. Startup HƯ CẤU, số liệu bịa cho đủ chỗ.
 * viDu = true nên PDF luôn in dải "VÍ DỤ MINH HỌA".
 */
export const HO_SO_VI_DU = chuanHoaHoSo({
  viDu: true,
  ten: 'Bếp Sổ',
  dinhVi: 'Phần mềm quản lý nguyên liệu và hao hụt cho chuỗi F&B từ 3 đến 30 điểm bán',
  nganh: 'SaaS B2B, F&B', giaiDoan: 'Seed', truSo: 'TP.HCM', thanhLap: '2024', website: 'bepso.vn',
  dot: 'Đợt 01, quý IV/2026',
  vanDe: 'Chuỗi F&B vừa và nhỏ vẫn kiểm kho bằng Excel và giấy, mỗi điểm bán mất khoảng 6 giờ mỗi tuần. Hao hụt nguyên liệu thường ở mức 8 đến 12% doanh thu mà chủ chuỗi không biết rơi ở khâu nào.',
  giaiPhap: 'Bếp Sổ nối máy POS với định lượng món, tự trừ kho theo từng đơn bán và báo ngay khi hao hụt vượt ngưỡng. Quản lý kiểm kho trên điện thoại trong 20 phút thay vì nửa ngày.',
  linkDemo: 'https://fundraising.vn',
  chiSo: [
    { giaTri: '180 tr đ', nhan: 'Doanh thu định kỳ tháng (MRR)', thoiDiem: '09/2026', nguon: 'doi-chieu' },
    { giaTri: '14%', nhan: 'Tăng trưởng MRR trung bình tháng, 6 tháng gần nhất', thoiDiem: '09/2026', nguon: 'tu-khai' },
    { giaTri: '62', nhan: 'Chuỗi trả phí, tổng 410 điểm bán', thoiDiem: '09/2026', nguon: 'doi-chieu' },
    { giaTri: '91%', nhan: 'Khách còn dùng sau 3 tháng', thoiDiem: 'Q3/2026', nguon: 'tu-khai' },
  ],
  doiNgu: [
    { ten: 'Trần Minh Khang', vaiTro: 'CEO', gioiThieu: '7 năm vận hành chuỗi cà phê 40 điểm bán, từng phụ trách cung ứng và kiểm soát chi phí.' },
    { ten: 'Lê Thu Hà', vaiTro: 'CTO', gioiThieu: 'Cựu trưởng nhóm kỹ thuật một nền tảng POS, xây hệ thống xử lý 2 triệu đơn mỗi tháng.' },
  ],
  soNhanSu: '14',
  vong: {
    quyMo: '600.000 USD', hinhThuc: 'SAFE', dinhGia: 'Cap 5 triệu USD', daCamKet: '150.000 USD từ 2 angel', runway: '7 tháng',
    dungVon: [{ muc: 'Sản phẩm', pt: 45 }, { muc: 'Bán hàng', pt: 35 }, { muc: 'Vận hành', pt: 20 }],
  },
  gocNhin: {
    diem: 74, ngayCham: '10/10/2026',
    diemManh: ['Founder hiểu sâu vận hành F&B, đo được hao hụt trước và sau khi dùng sản phẩm.', 'Doanh thu đã đối chiếu với sao kê, tỷ lệ giữ chân cao so với SaaS cho SME.'],
    cauHoi: ['Chi phí có một khách hàng (CAC) và thời gian hoàn vốn theo kênh bán?', 'Kế hoạch mở rộng ra ngoài TP.HCM cần đội bán hàng lớn đến đâu?'],
  },
});
